import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import PageHeader from "../PageHeader";
import { trainingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import LiftChart from "./LiftChart";
import MuscleWeek from "./MuscleWeek";
import { dayKey } from "./lib/format";
import { openTables } from "./lib/history";
import { chartedLifts } from "./lib/progress";

/**
 * Anything to show: a lift's point, or a muscle ever trained. Any logged
 * set trains its exercise's primary muscles, so only sessions with no sets
 * at all leave nothing — and then there is nothing to show.
 */
function hasHistory(summary) {
  return (
    (summary?.main_lifts ?? []).some((lift) => (lift.trend?.length ?? 0) > 0) ||
    (summary?.muscles ?? []).some((muscle) => muscle.last_trained_at)
  );
}

/**
 * Progress (spec §6, D1): this week's sets per muscle against its target,
 * and each main lift's estimated max over 26 weeks — all from one GET
 * summary/. Numbers are in summary/'s units; days are calendar days in the
 * settings' zone.
 *
 * States: loading; a failed load (what failed, and a Retry that asks
 * again — it stays on screen, saying "Retrying…", until the answer is in,
 * so focus isn't dropped; a success then moves focus to the page's first
 * heading); nothing yet (what will show, and after what); the page.
 *
 * Which lifts' tables are open lives in the URL (`?table=bench-press`, one
 * per lift, replaced rather than pushed): the table is the keyboard and
 * screen-reader path through the sessions, so Back from one finds it open,
 * with focus on its toggle once the page is in.
 */
function TrainingProgress() {
  const { settings, exercises } = useOutletContext() || {};
  const timeZone = settings?.timezone;
  const id = useId();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const tables = openTables(location.search);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  // Failed loads so far: each failure is a new alert (keyed by it), so a
  // Retry that fails again — even with the same words — is announced again.
  const [failures, setFailures] = useState(0);
  const request = useRef(0);
  const retried = useRef(false);
  const landing = useRef(null);
  const retryButton = useRef(null);
  const liftsHeading = useRef(null);
  const page = useRef(null);
  // The table to come back to: the last one the URL opens, focused once
  // the first answer is in (a Retry's landing goes to the heading instead).
  const restore = useRef(tables.at(-1) ?? null);

  useEffect(() => {
    const ask = ++request.current;
    setLoading(true);
    (async () => {
      try {
        const { data } = await trainingApi.summary();
        if (ask !== request.current) return; // a newer request (or a left page) owns the screen
        setSummary(data);
        setError(null);
      } catch (err) {
        if (ask !== request.current) return;
        setError(apiErrorMessage(err, "Something went wrong."));
        setFailures((count) => count + 1);
      }
      setLoading(false);
    })();
    return () => {
      request.current += 1;
    };
  }, [reloadTick]);

  // After a Retry lands, focus goes somewhere on purpose: the page's first
  // heading when it worked (the Retry button is gone); when it failed again,
  // the new alert's Retry (the alert is a new one, so it's announced).
  useEffect(() => {
    if (loading || !retried.current) return;
    retried.current = false;
    (landing.current ?? retryButton.current)?.focus();
  }, [loading]);

  useEffect(() => {
    if (summary === null || restore.current === null) return;
    const key = restore.current;
    restore.current = null;
    const toggles = page.current?.querySelectorAll("[data-table-toggle]") ?? [];
    [...toggles].find((toggle) => toggle.dataset.tableToggle === key)?.focus();
  }, [summary]);

  const retry = () => {
    if (loading) return; // one at a time: the button says "Retrying…"
    retried.current = true;
    restore.current = null;
    setReloadTick((tick) => tick + 1);
  };

  const byKey = useMemo(() => new Map((exercises ?? []).map((e) => [e.key, e])), [exercises]);
  // "today" and "last trained" are read when the answer lands
  const now = useMemo(() => new Date(), [summary]); // eslint-disable-line react-hooks/exhaustive-deps
  const todayKey = dayKey(now, timeZone);
  const units = summary?.units ?? settings?.units ?? "lb";
  const lifts = chartedLifts(summary?.main_lifts, settings?.vertical_pull);

  // One URL write from the URL as it is now: open adds the lift, close drops it.
  const toggleTable = (exercise) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        const open = next.getAll("table");
        next.delete("table");
        const keep = open.includes(exercise) ? open.filter((key) => key !== exercise) : [...open, exercise];
        for (const key of keep) next.append("table", key);
        return next;
      },
      { replace: true },
    );

  // A section a tap away (the charts sit ~3,300 px down on a phone): its
  // heading takes focus, without a second scroll.
  const jumpTo = (heading) => {
    heading?.closest("section")?.scrollIntoView?.({ block: "start" });
    heading?.focus({ preventScroll: true });
  };

  let body;
  if (error) {
    body = (
      <Alert key={failures} variant="destructive">
        <AlertDescription className="flex flex-wrap items-center gap-3">
          <span className="flex-1">Couldn&apos;t load your progress: {error}</span>
          <Button
            ref={retryButton}
            type="button"
            size="sm"
            variant="outline"
            className="h-11 md:h-8"
            aria-disabled={loading || undefined}
            onClick={retry}
          >
            {loading ? "Retrying…" : "Retry"}
          </Button>
        </AlertDescription>
      </Alert>
    );
  } else if (loading || summary === null) {
    body = (
      <div className="py-12 text-center" role="status">
        <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">Loading your progress…</span>
      </div>
    );
  } else if (!hasHistory(summary)) {
    body = (
      <section aria-labelledby={`${id}-empty`} className="rounded-lg border bg-card p-4 md:p-6">
        <h2
          id={`${id}-empty`}
          ref={landing}
          tabIndex={-1}
          className="mt-0 mb-2 font-display text-xl font-semibold focus-visible:outline-none"
        >
          Nothing to show yet
        </h2>
        <p className="mt-0 mb-4 text-muted-foreground">
          After your first session, this page shows each muscle&apos;s sets this week against its weekly target, how
          recovered it is and when you last trained it. Log a main lift — squat, bench, overhead press, row, chin-up
          or pulldown — with the reps you had in reserve, and its estimated max starts a chart here, a point per
          session.
        </p>
        <Button asChild className="h-11 w-full md:h-9 md:w-auto">
          <Link to="/training">Go to Today</Link>
        </Button>
      </section>
    );
  } else {
    body = (
      <>
        {/* Buttons, not #hash links — the page's URL keeps its tables (W20's pattern). */}
        <nav aria-label="Jump to a section" className="-my-3 flex flex-wrap gap-x-1 text-sm">
          <Button type="button" variant="link" className="h-11 px-2 md:h-8" onClick={() => jumpTo(landing.current)}>
            This week
          </Button>
          <Button
            type="button"
            variant="link"
            className="h-11 px-2 md:h-8"
            onClick={() => jumpTo(liftsHeading.current)}
          >
            Estimated max
          </Button>
        </nav>
        <MuscleWeek muscles={summary.muscles ?? []} timeZone={timeZone} now={now} headingRef={landing} />
        <section aria-labelledby={`${id}-lifts`} className="flex scroll-mt-24 flex-col gap-3">
          <div>
            <h2
              id={`${id}-lifts`}
              ref={liftsHeading}
              tabIndex={-1}
              className="m-0 font-display text-xl font-semibold focus-visible:outline-none"
            >
              Estimated max
            </h2>
            <p className="mt-1 mb-0 text-sm text-muted-foreground">
              Each main lift&apos;s best set per session over the last 26 weeks, as an estimated one-rep max from its
              load, reps and reps in reserve. Sets logged without reps in reserve aren&apos;t counted.
            </p>
          </div>
          <ul aria-labelledby={`${id}-lifts`} className="m-0 flex list-none flex-col gap-3 p-0">
            {lifts.map((lift) => (
              <LiftChart
                key={lift.exercise}
                lift={lift}
                units={units}
                exercise={byKey.get(lift.exercise)}
                todayKey={todayKey}
                tableOpen={tables.includes(lift.exercise)}
                onToggleTable={toggleTable}
                from={location.search}
              />
            ))}
          </ul>
        </section>
      </>
    );
  }

  return (
    <div className="page-container mt-6 pt-4 pb-8">
      <div ref={page} className="mx-auto flex max-w-2xl flex-col gap-6">
        <PageHeader
          title="Progress"
          subtitle="This week's sets per muscle against its target, and how each main lift's estimated max has moved."
          className="mb-0"
        />
        {body}
      </div>
    </div>
  );
}

export default TrainingProgress;
